import { useCallback } from 'react';
import { formatDateForFilename, sanitizeFilename } from '@orca360/shared';
import { useDocumentAPI } from './useDocumentAPI';
import type { DebriefingPartAData } from '../types/debriefing-export';
import type {
  PartB_DebriefingFramework,
  PartC_SupportingMaterial,
  PartD_InstructorNotes,
} from '../types/debriefing-guide';

export interface DebriefingGuideExportData {
  partA: DebriefingPartAData;
  partB?: PartB_DebriefingFramework;
  partC?: PartC_SupportingMaterial;
  partD?: PartD_InstructorNotes;
  invocationLine?: string;
}

export interface UsePDFExportDebriefingOptions {
  onExportStart?: () => void;
  onExportSuccess?: (blob: Blob, filename: string) => void;
  onExportError?: (error: Error) => void;
  includePartA?: boolean;
  includePartB?: boolean;
  includePartC?: boolean;
  includePartD?: boolean;
  includePartE?: boolean;
}

export interface UsePDFExportDebriefingReturn {
  exportDebriefingToPDF: (data: DebriefingGuideExportData, sessionId?: string) => Promise<Blob>;
  isExporting: boolean;
  error: Error | null;
}

export function usePDFExportDebriefing(
  options: UsePDFExportDebriefingOptions = {},
): UsePDFExportDebriefingReturn {
  const { generateDocument, isExporting, error } = useDocumentAPI(options);

  const exportDebriefingToPDF = useCallback(
    async (data: DebriefingGuideExportData, sessionId?: string): Promise<Blob> => {
      const sessionDate = formatDateForFilename(data.partA?.sessionDate || 'export');
      const traineeName = sanitizeFilename(data.partA?.trainee?.name || 'trainee');
      const defaultFilename = `WingMentor_Debriefing_${sessionDate}_${traineeName}.pdf`;
      // sessionId lets the endpoint resolve the owning tenant's effective
      // framework version for authority exports (HG-2B G2-05d).
      return generateDocument('debriefing-guide', 'pdf', data as unknown as Record<string, unknown>, defaultFilename, sessionId);
    },
    [generateDocument],
  );

  return { exportDebriefingToPDF, isExporting, error };
}
